import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { interval, Observable, of, switchMap, take, takeWhile } from 'rxjs';
import { AppConfigService } from '../config.service';
import { IPaymentGateway } from './payment-gateway.interface';
import { PaymentProcessorResponse, PaymentRequest, PaymentStatusResponse, PaymentVerifyDto } from '@core/models/payment';
import { SKIP_ERROR_MODAL } from '@core/interceptors/error.interceptor';
import { environment } from '@env/environment';

@Injectable({ providedIn: 'root' })
export class PaymentGatewayService implements IPaymentGateway {
  private readonly terminalApiHost = environment.paymentGatewayUrl;
  private readonly http = inject(HttpClient);
  private readonly appConfigService = inject(AppConfigService);
  readonly canCancelPayment = signal(true);

  private get gatewayType(): string {
    return this.appConfigService.currentConfig().paymentProvider ?? 'adyen';
  }

  private get host(): string {
    return this.appConfigService.host();
  }

  createPaymentRequest(request: PaymentRequest): Observable<any> {
    return this.http.post(`${this.host}/payments/requests`, request);
  }

  setPaymentProcessor(requestId: string, gatewayId: string): Observable<PaymentProcessorResponse> {
    return this.http.post<PaymentProcessorResponse>(
      `${this.host}/payments/requests/${requestId}/processor`,
      { paymentGatewayId: gatewayId }
    );
  }

  sendPaymentToTerminal(requestId: string, gatewayId: string, terminalId?: string): Observable<any> {
    const terminal = terminalId ?? this.appConfigService.currentConfig().terminalId;
    return this.http.post<{ id: string; status: string }>(
      `${this.terminalApiHost}/payments/terminals/process`,
      {
        paymentGatewayId: gatewayId,
        paymentRequestId: requestId,
        paymentGatewayType: this.gatewayType,
        terminalId: terminal,
        mode: 'card',
      },
      { context: new HttpContext().set(SKIP_ERROR_MODAL, true) }
    );
  }

  checkPaymentStatus(requestId: string, gatewayId: string, terminalId?: string, processorId?: string, processorReference?: string): Observable<PaymentStatusResponse> {
    this.canCancelPayment.set(true);
    const endpoint = `${this.terminalApiHost}/payments/terminals/process/${processorId}?paymentGatewayId=${gatewayId}&paymentGatewayType=${this.gatewayType}`;
    const verifyDto: PaymentVerifyDto = {
      paymentGatewayId: gatewayId,
      data: {
        reference: processorReference,
        terminalNumber: terminalId,
      },
    };
    const verifyProcessor$ = this.http.post<PaymentStatusResponse>(
      `${this.host}/payments/requests/${requestId}/verify`,
      verifyDto
    );
    return interval(1000).pipe(
      switchMap(() => this.http.get<PaymentStatusResponse>(endpoint)),
      takeWhile((res) => res.status === 'PENDING', true),
      switchMap((res) => {
        if (res.status === 'SUCCEEDED') {
          this.canCancelPayment.set(false);
          return interval(3000).pipe(
            take(6), // 6 attempts maximum
            switchMap(() => verifyProcessor$),
            takeWhile((verifyResponse) => verifyResponse.status !== 'SUCCEEDED', true)
          );
        }
        return of(res);
      })
    );
  }

  cancelPayment(gatewayId: string, processorId?: string): Observable<any> {
    return this.http.post(
      `${this.terminalApiHost}/payments/terminals/process/${processorId}/cancel?paymentGatewayId=${gatewayId}&paymentGatewayType=${this.gatewayType}`,
      { id: processorId }
    );
  }
}
